import React from 'react';
import cn from 'classnames';

import CrossIcon from 'widget/shared/icons/CrossIcon';
import PlayIcon from 'widget/shared/icons/PlayIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';
import { formatFileSize, getFileExtension } from 'widget/utils/formatters';
import FileIcon from 'widget/shared/icons/FileIcon';
import { WidgetTexts } from 'widget/consts/texts';
import IconButton from 'widget/shared/ui/IconButton';

import styles from './EditableAttachmentCard.module.scss';

export type EditableAttachmentCardProps = {
  filename: string;
  size: number;
  previewUrl?: string;
  isVideo?: boolean;
  onRemove: () => void;
  onRetry?: () => void;
  isError?: boolean;
  isLoading?: boolean;
  errorText?: string;
};

const EditableAttachmentCard: React.FC<EditableAttachmentCardProps> = ({
  filename,
  size,
  previewUrl,
  isVideo = false,
  onRemove,
  onRetry,
  isError = false,
  isLoading = false,
  errorText
}) => {
  const extension = getFileExtension(filename);
  const secondaryText = errorText ?? formatFileSize(size);
  const [previewError, setPreviewError] = React.useState(false);

  const handleRemove = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onRemove();
    },
    [onRemove]
  );

  const handleRetry = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onRetry?.();
    },
    [onRetry]
  );

  const handlePreviewError = React.useCallback(() => {
    setPreviewError(true);
  }, []);

  const showImage = previewUrl && !previewError;

  const renderPreview = () => {
    return (
      <div className={styles['card__preview']}>
        {showImage ? (
          <>
            <img
              className={styles['card__image']}
              src={previewUrl}
              alt={filename}
              onError={handlePreviewError}
            />
            {isVideo && (
              <PlayIcon
                className={styles['card__play-icon']}
                color={IconColor.Main}
                size={IconSize.XS}
              />
            )}
          </>
        ) : (
          <FileIcon
            extension={extension || 'FILE'}
            className={styles['card__icon']}
          />
        )}
      </div>
    );
  };

  const renderSecondaryText = () => {
    if (isLoading) {
      return (
        <span className={styles['card__loading-text']}>
          {WidgetTexts.messageForm.attachments.loading}
        </span>
      );
    }

    if (isError && onRetry) {
      return (
        <>
          {secondaryText}{' '}
          <button
            className={styles['card__retry']}
            type="button"
            onClick={handleRetry}
          >
            {WidgetTexts.messageForm.attachments.retry}
          </button>
        </>
      );
    }
    return secondaryText;
  };

  return (
    <div className={cn(styles['card'], isError && styles['card_error'])}>
      {renderPreview()}
      <div className={styles['card__info']}>
        <span className={styles['card__filename']}>{filename}</span>
        <span
          className={cn(
            styles['card__size'],
            isError && styles['card__size_error']
          )}
        >
          {renderSecondaryText()}
        </span>
      </div>
      <IconButton
        className={styles['card__remove']}
        onClick={handleRemove}
        aria-label={WidgetTexts.messageForm.attachments.removeLabel}
      >
        <CrossIcon size={IconSize.XS} />
      </IconButton>
    </div>
  );
};

export default React.memo(EditableAttachmentCard);
